import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useOpenShifts } from "@/hooks/useOpenShifts";

/** Where the chosen department's posted requests stand, for a gaps screen that lists them. */
export enum GapsOpenShiftsLoad {
	/** This department's first load has not settled: the list is not "empty" yet, only unknown. */
	Loading = "loading",
	/** The load failed and there is no earlier answer for this department to fall back on — a 403, the network. */
	Failed = "failed",
	/** This department's requests are known (a later background refetch may have failed; the last answer stands). */
	Ready = "ready",
}

/**
 * The outcome of the chosen department's open-shifts load, so a list of requests can tell "nothing is posted" apart
 * from "we could not find out" and from "not known yet" — `RequestsContext` only ever hands out a list.
 *
 * Reads the provider's own query (same key, `useOpenShifts`), never fetching on its own: the gaps view is for head
 * nurses, whose feed the provider keys by the chosen department, exactly as here. It is decided by that query alone,
 * never by the context's list: after a department switch the context keeps the previous department's requests until
 * the new department's data lands, so a non-empty list proves nothing about this department. The query sets no
 * `placeholderData`, so any data it holds is this department's own.
 * - Data → Ready, even when a later refetch failed: the last good answer, an empty one included, is still the answer
 *   (the roster-failed rule on the home, for the same reason).
 * - No data and the load failed → Failed.
 * - Otherwise → Loading.
 */
export const useGapsOpenShiftsLoad = (): GapsOpenShiftsLoad => {
	const { chosenDepartment } = useHospitalData();
	const { data, isError } = useOpenShifts(chosenDepartment.department_id);
	if (data !== undefined) return GapsOpenShiftsLoad.Ready;
	return isError ? GapsOpenShiftsLoad.Failed : GapsOpenShiftsLoad.Loading;
};
